import { Injectable } from '@angular/core';
import { PpHttpParams, PpHttpService } from '@penpencil/common';
import { Observable } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

import { ErrorHandlerService } from './error-handler.service';
import { Chapter, ClassModel, Section, Stream, Subject } from './qbg-models';
import { ExamCategoryModel } from './qbg-models/exam-category.model';
import { TargetExamCategory } from './qbg-models/target-exam-category.model';
import { XCategoryTagsModel } from './qbg-models/x-category-tags.model';

@Injectable({
    providedIn: 'root',
})
export class QbgService extends ErrorHandlerService {
    constructor(private _ppHttpService: PpHttpService) {
        super();
    }

    getExamCategory() {
        return this._ppHttpService
            .get<Array<ExamCategoryModel>>({
                apiPath: 'v1/admin/qbg/category-configuration',
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getExamByCategory(
        category_id: string,
        page: number = 1,
        limit: number = 20,
    ) {
        return this._ppHttpService
            .get<Array<TargetExamCategory>>({
                apiPath: 'v1/admin/qbg/target-exam',
                params: new PpHttpParams()
                    .append('category_id', category_id)
                    .append('page', page.toString())
                    .append('limit', limit.toString()),
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getXCategoryTags(category_id: string) {
        return this._ppHttpService
            .get<XCategoryTagsModel[]>({
                apiPath: 'v1/admin/qbg/x-category-tags-mapping',
                params: new PpHttpParams().append('categoryId', category_id),
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getClasses() {
        return this._ppHttpService
            .get<ClassModel>({
                apiPath: 'v1/admin/qbg/classes',
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getClassesByExamCategory(category_id: string) {
        return this._ppHttpService
            .get<Array<ClassModel>>({
                apiPath: 'v1/admin/qbg/classes',
                params: new PpHttpParams().append('category_id', category_id),
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getStreams() {
        return this._ppHttpService
            .get<Stream>({
                apiPath: 'v1/admin/qbg/streams',
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getSectionByStream(stream_id: string) {
        return this._ppHttpService
            .get<Section>({
                apiPath: 'v1/admin/qbg/sections',
                params: new PpHttpParams().append('stream_id', stream_id),
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getSubjects(query: any) {
        return this._ppHttpService
            .get<Array<Subject>>({
                apiPath: 'v1/admin/qbg/subjects/getAllSubjects',
                params: new PpHttpParams().appendAll(query),
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getChapters(query: any) {
        const options = {
            apiPath: 'v1/admin/qbg/chapters/getAllChapters',
            params: new PpHttpParams().appendAll(query),
        };
        return this._ppHttpService.get<Chapter>(options).pipe(
            retry(1),
            catchError(this.handleError),
            map((res: any) => res['data']),
        );
    }

    getQbgSubjects(): Observable<any> {
        return this._ppHttpService
            .get({
                apiPath: 'v1/admin/qbg/subjects',
            })
            .pipe(catchError(this.handleError));
    }

    getSubjectsByExamCategory(
        category_id: string,
        class_id: string = '',
    ): Observable<any> {
        let params = new PpHttpParams().append('category_id', category_id);
        if (class_id) {
            params = params.append('class_id', class_id);
        }
        return this._ppHttpService
            .get<Array<Subject>>({
                apiPath: 'v1/admin/qbg/subjects',
                params: params,
            })
            .pipe(
                retry(1),
                catchError(this.handleError),
                map((res: any) => res['data']),
            );
    }

    getQbgChapters(subjectId: string): Observable<any> {
        return this._ppHttpService
            .get({
                apiPath: 'v1/admin/qbg/chapters',
                params: new PpHttpParams().append('subject_id', subjectId),
            })
            .pipe(catchError(this.handleError));
    }

    getQbgTopics(subjectId: string, chapterId: string): Observable<any> {
        return this._ppHttpService
            .get({
                apiPath: 'v1/admin/qbg/topics',
                params: new PpHttpParams()
                    .append('subject_id', subjectId)
                    .append('chapter_id', chapterId),
            })
            .pipe(catchError(this.handleError));
    }

    getQbgSubTopics(
        subjectId: string,
        chapterId: string,
        topicId: string,
    ): Observable<any> {
        return this._ppHttpService
            .get({
                apiPath: 'v1/admin/qbg/subtopics',
                params: new PpHttpParams()
                    .append('subject_id', subjectId)
                    .append('chapter_id', chapterId)
                    .append('topic_id', topicId),
            })
            .pipe(catchError(this.handleError));
    }

    getChapterBySubjectIds(
        subjectIds: any[],
        page = 1,
        limit = 20,
    ): Observable<any> {
        const data = subjectIds.map((subjectId: string) => ({
            // eslint-disable-next-line @typescript-eslint/naming-convention
            subject_id: subjectId,
        }));
        return this._ppHttpService
            .post(data, {
                apiPath: `v1/admin/qbg/subjects/get-chapters-by-subjectId?page=${page}&limit=${limit}`,
            })
            .pipe(catchError(this.handleError));
    }

    getChapterDetailById(chapterId: string): Observable<any> {
        return this._ppHttpService
            .get({
                apiPath: `v1/admin/qbg/chapters/details/${chapterId}-123456`,
            })
            .pipe(catchError(this.handleError));
    }
}
